// 年報ポスター画面
// 期間を選んで、その期間の読了書籍から AI が年報テキストを生成。
// Canvas で 1080x1920 (9:16) のポスター画像を描画し、Web Share API で共有可能。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
} from '../ui.js';
import { fetchYearReport } from '../api.js';
import { getAllBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

export async function showYearReportScreen() {
  const histItems = await getAiHistoryByType('year-report');
  renderEntry(histItems);
}

function renderEntry(histItems) {
  const now = new Date();
  const year = now.getFullYear();
  const html = `
    ${screenHead('年報ポスター')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      期間を選ぶと、その期間の読書を 1 枚のポスター（9:16）に凝縮します。<br/>
      Instagram ストーリーズ等への共有に最適化されています。
    </p>
    <div class="form-group">
      <label class="form-label">期間</label>
      <select class="form-select" id="yr-period">
        <option value="year-${year}">${year} 年（全体）</option>
        <option value="half1-${year}">${year} 年 上半期</option>
        <option value="half2-${year}">${year} 年 下半期</option>
        <option value="year-${year - 1}">${year - 1} 年</option>
        <option value="recent-180">直近 半年</option>
        <option value="recent-90">直近 3 ヶ月</option>
        <option value="recent-30">直近 1 ヶ月</option>
      </select>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>年報ポスターを作成する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1 回あたり 約 5-10 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('year-report', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    await runReport(screen.querySelector('#yr-period').value);
  });
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderPoster(entry.payload, entry.context?.label || ''),
    () => showYearReportScreen()
  );
}

function periodToRange(value) {
  const m = value.match(/^year-(\d{4})$/);
  if (m) {
    const y = parseInt(m[1], 10);
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59), label: `${y} 年` };
  }
  const h = value.match(/^half(\d)-(\d{4})$/);
  if (h) {
    const half = parseInt(h[1], 10);
    const y = parseInt(h[2], 10);
    if (half === 1) return { start: new Date(y, 0, 1), end: new Date(y, 5, 30, 23, 59, 59), label: `${y} 年 上半期` };
    return { start: new Date(y, 6, 1), end: new Date(y, 11, 31, 23, 59, 59), label: `${y} 年 下半期` };
  }
  const r = value.match(/^recent-(\d+)$/);
  if (r) {
    const days = parseInt(r[1], 10);
    const end = new Date();
    const start = new Date(end.getTime() - days * 86400000);
    return { start, end, label: `直近 ${days} 日` };
  }
  return null;
}

async function runReport(periodValue) {
  const range = periodToRange(periodValue);
  if (!range) return;
  const all = await getAllBooks();
  const inRange = all.filter((b) => {
    if (!b.endDate) return false;
    const d = new Date(b.endDate);
    return d >= range.start && d <= range.end;
  });
  if (inRange.length < 2) {
    toast(`${range.label} に読了記録が少なすぎます（${inRange.length} 冊）`);
    return;
  }
  // ジャンル集計
  const genreCount = new Map();
  for (const b of inRange) {
    for (const g of b.genre || []) genreCount.set(g, (genreCount.get(g) || 0) + 1);
  }
  const topGenres = [...genreCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([g]) => g);

  showLoading('AI が年報を編集中…');
  try {
    const payload = inRange.map((b) => ({
      name: b.name,
      author: b.author,
      genre: b.genre,
      note: b.note,
      rating: b.rating,
      endDate: b.endDate,
    }));
    const periodSummary = { total: inRange.length, topGenres };
    const result = await fetchYearReport(payload, range.label, periodSummary);
    const enriched = { ...result, period: range.label, total: inRange.length, topGenres, books: inRange };
    await saveAiHistory({
      type: 'year-report',
      title: `${range.label} (${inRange.length}冊)`,
      subtitle: result.headline || '',
      context: { label: range.label },
      payload: enriched,
    });
    renderPoster(enriched, range.label);
  } catch (e) {
    console.error(e);
    toast('年報の生成に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderPoster(entry.payload, entry.context?.label || '');
}

function renderPoster(data, label) {
  const html = `
    ${screenHead('年報ポスター')}
    <div style="margin:0 0 20px;text-align:center;">
      <canvas id="yr-canvas" width="1080" height="1920" style="max-width:100%;height:auto;border:1px solid var(--line);background:var(--paper);"></canvas>
    </div>
    <div style="display:flex;flex-direction:column;gap:8px;">
      <button class="btn btn--ink btn--block" id="btn-share" type="button">
        <svg class="icon"><use href="#i-arrow-right"/></svg>
        <span>共有する (Instagram など)</span>
      </button>
      <button class="btn btn--block" id="btn-download" type="button">
        <svg class="icon"><use href="#i-download"/></svg>
        <span>画像を保存</span>
      </button>
      <button class="btn btn--block btn--ghost" id="btn-back-form" type="button" style="margin-top:8px;">
        <svg class="icon"><use href="#i-refresh"/></svg>
        <span>別の期間で作る</span>
      </button>
    </div>
  `;
  const screen = showScreen('year-report', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());

  const canvas = screen.querySelector('#yr-canvas');
  drawPoster(canvas, data, label).then(() => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const filename = `lectio-${label.replace(/\s+/g, '-')}-${Date.now()}.png`;
        screen.querySelector('#btn-download').addEventListener('click', () => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        });
        screen.querySelector('#btn-share').addEventListener('click', async () => {
          await sharePoster(blob, filename, data);
        });
      },
      'image/png'
    );
  });

  screen.querySelector('#btn-back-form').addEventListener('click', () => showYearReportScreen());
}

async function sharePoster(blob, filename, data) {
  const file = new File([blob], filename, { type: 'image/png' });
  const shareData = {
    files: [file],
    title: `lectio · ${data.period}`,
    text: data.headline || '',
  };
  if (navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      return;
    } catch (e) {
      if (e.name !== 'AbortError') console.error('share failed:', e);
    }
  }
  // フォールバック: ダウンロード
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast('画像を保存しました。Instagram で「+」→ ストーリーズで開いてください');
}

// ===== Canvas 描画 =====
async function drawPoster(canvas, data, label) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  // 背景（クリーム紙風）
  ctx.fillStyle = '#f4f4f2';
  ctx.fillRect(0, 0, W, H);

  // 上部のヘアライン
  ctx.strokeStyle = '#0a0a0a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(120, 200);
  ctx.lineTo(W - 120, 200);
  ctx.stroke();

  // ブランドとピリオド
  ctx.fillStyle = '#0a0a0a';
  ctx.textAlign = 'left';
  ctx.font = '500 38px "Noto Sans JP", system-ui, sans-serif';
  ctx.fillText('LECTIO', 120, 140);
  ctx.textAlign = 'right';
  ctx.font = '300 30px "Noto Sans JP", system-ui, sans-serif';
  ctx.fillStyle = '#5a5a58';
  ctx.fillText('annual report', W - 120, 140);

  // 期間
  ctx.textAlign = 'center';
  ctx.fillStyle = '#5a5a58';
  ctx.font = '300 34px "Noto Serif JP", serif';
  ctx.fillText(label, W / 2, 270);

  // headline （マージン適正化：上の期間から 90px、下の数字まで 120px の余白）
  const headline = data.headline || '一年の読書';
  ctx.fillStyle = '#0a0a0a';
  ctx.font = '500 60px "Noto Serif JP", serif';
  drawWrappedText(ctx, headline, W / 2, 380, W - 240, 80, 'center', 2);

  // ヘアライン
  ctx.strokeStyle = '#ededea';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(120, 580);
  ctx.lineTo(W - 120, 580);
  ctx.stroke();

  // total + topGenres （headline と詰める）
  ctx.textAlign = 'center';
  ctx.fillStyle = '#0a0a0a';
  ctx.font = '500 110px "Noto Serif JP", serif';
  ctx.fillText(`${data.total || 0}`, W / 2, 720);
  ctx.font = '300 30px "Noto Sans JP", sans-serif';
  ctx.fillStyle = '#5a5a58';
  ctx.fillText('books read', W / 2, 768);

  // topGenres
  if (data.topGenres && data.topGenres.length) {
    ctx.font = '300 26px "Noto Sans JP", sans-serif';
    ctx.fillStyle = '#0a0a0a';
    const genres = data.topGenres.slice(0, 3).join(' · ');
    ctx.fillText(genres, W / 2, 822);
  }

  // profile（読者像）
  ctx.strokeStyle = '#ededea';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(120, 880);
  ctx.lineTo(W - 120, 880);
  ctx.stroke();

  ctx.textAlign = 'left';
  ctx.fillStyle = '#5a5a58';
  ctx.font = '500 18px "Noto Sans JP", sans-serif';
  ctx.fillText('PROFILE', 120, 928);

  ctx.fillStyle = '#0a0a0a';
  ctx.font = '400 28px "Noto Serif JP", serif';
  drawWrappedText(ctx, data.profile || '', 120, 980, W - 240, 46, 'left', 4);

  // highlights（profile 4行=1164 まで → 1210 開始）
  const highlightsY = 1210;
  ctx.textAlign = 'left';
  ctx.fillStyle = '#5a5a58';
  ctx.font = '500 18px "Noto Sans JP", sans-serif';
  ctx.fillText('HIGHLIGHTS', 120, highlightsY);

  const highlights = (data.highlights || []).slice(0, 3);
  let hy = highlightsY + 30;
  for (const h of highlights) {
    ctx.fillStyle = '#0a0a0a';
    ctx.font = '500 26px "Noto Serif JP", serif';
    ctx.textAlign = 'left';
    ctx.fillText(`「${h.book || ''}」`, 120, hy + 28);
    ctx.fillStyle = '#5a5a58';
    ctx.font = '300 21px "Noto Serif JP", serif';
    drawWrappedText(ctx, h.oneLine || '', 120, hy + 62, W - 240, 30, 'left', 2);
    hy += 115;
  }

  // motto + prediction エリア（下部に十分余白を確保。motto 線まで 90px 余白）
  const mottoLineY = H - 300;
  ctx.strokeStyle = '#0a0a0a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(120, mottoLineY);
  ctx.lineTo(W - 120, mottoLineY);
  ctx.stroke();

  if (data.motto) {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#0a0a0a';
    ctx.font = '500 38px "Noto Serif JP", serif';
    drawWrappedText(ctx, `『${data.motto}』`, W / 2, mottoLineY + 85, W - 240, 52, 'center', 2);
  }

  if (data.prediction) {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#5a5a58';
    ctx.font = '300 21px "Noto Serif JP", serif';
    drawWrappedText(ctx, data.prediction, W / 2, mottoLineY + 170, W - 240, 30, 'center', 2);
  }

  // フッタ
  ctx.textAlign = 'center';
  ctx.fillStyle = '#8a8a86';
  ctx.font = '300 18px "Noto Sans JP", sans-serif';
  ctx.fillText('— lectio · the reading curator —', W / 2, H - 50);
}

// 折り返し描画（簡易・日本語対応：1 文字ずつ計測）
function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, align = 'left', maxLines = 0) {
  if (!text) return;
  const chars = [...String(text)];
  const lines = [];
  let line = '';
  for (const ch of chars) {
    const next = line + ch;
    if (ctx.measureText(next).width > maxWidth && line.length > 0) {
      lines.push(line);
      line = ch;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  const limited = maxLines > 0 ? lines.slice(0, maxLines) : lines;
  // 最終行が切り詰められた場合は「…」を加える
  if (maxLines > 0 && lines.length > maxLines) {
    limited[maxLines - 1] = limited[maxLines - 1].slice(0, -1) + '…';
  }
  ctx.textAlign = align;
  for (let i = 0; i < limited.length; i++) {
    ctx.fillText(limited[i], x, y + i * lineHeight);
  }
}
